import type { ColumnDef } from '@tanstack/react-table'
import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import type { PaginationConfig } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/shared/ui/data-table'
import { TicketFeatureStatusToggle } from '@/features/ticket-feature/ticket-feature-status-toggle'

function resolveFeatureName(
  localizedData: ITicketFeature['localizedData'],
  languageCode: string,
  featureKey: string,
): string {
  const preferred = localizedData[languageCode]
  if (preferred?.name?.trim()) return preferred.name.trim()
  if (preferred?.description?.trim()) return preferred.description.trim()
  for (const v of Object.values(localizedData)) {
    if (v?.name?.trim()) return v.name.trim()
  }
  for (const v of Object.values(localizedData)) {
    if (v?.description?.trim()) return v.description.trim()
  }
  return featureKey
}

interface TicketFeatureListTableProps {
  data: ITicketFeature[]
  loading: boolean
  pagination: PaginationConfig
  languageCode: string
  onEdit: (feature: ITicketFeature) => void
}

export function TicketFeatureListTable({
  data,
  loading,
  pagination,
  languageCode,
  onEdit,
}: TicketFeatureListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<ITicketFeature>[]>(
    () => [
      {
        accessorKey: 'featureKey',
        header: t('ticketFeature.featureKey', { defaultValue: 'Feature Key' }),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.featureKey}</span>,
      },
      {
        id: 'name',
        header: t('common.name'),
        cell: ({ row }) =>
          resolveFeatureName(row.original.localizedData, languageCode, row.original.featureKey),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <TicketFeatureStatusToggle featureId={row.original.id} isActive={row.original.isActive} />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('common.createdAt'),
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ],
    [languageCode, onEdit, t],
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      onRowClick={onEdit}
    />
  )
}
